import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSpring, animated } from 'react-spring';
import { Switch, FormControlLabel, Box, Typography, Button, IconButton, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, ToggleButtonGroup, ToggleButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import upIcon from '../assets/up_icon.png';
import downIcon from '../assets/down_icon.png';
import './../BottomSheet.css';
import { useDrag } from 'react-use-gesture';
import swal from 'sweetalert';
import { WebSocketContext } from "../WebSocketContext";
import { useTheme } from '../contexts/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomToast from './CustomToast';
import { blue } from '@mui/material/colors';
import config from '../config';

const BottomSheetMini = ({ userDetails, stock,_stock, isOpen, onClose }) => {
  const { refreshTradesAndPortfolio, submitRejectionLog } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const [disableBtn, setDisable] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderResponse, setOrderResponse] = useState(null);
  const [showOrderResponse, setShowOrderResponse] = useState(false);
  const styles = useSpring({
    transform: isOpen ? 'translateY(0%)' : 'translateY(100%)',
  });

  // console.log("BottomSheet",stock);

  const showToast = (type, message) => {
    if(type==1){
      toast.success(<CustomToast title={"Trade Placed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==2){
      toast.success(<CustomToast title={"Order Placed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==3){
      toast.error(<CustomToast title={"Order Failed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
};

  const [{ y }, api] = useSpring(() => ({ y: 0 }));

  React.useEffect(() => {
    api.start({ y: isOpen ? 0 : 100 });
    // console.log("userDetails",userDetails.FUTCOM_margin_type );
  }, [isOpen, api]);

  // const bind = useDrag(
  //   ({ down, movement: [, my], direction: [, dy], velocity }) => {
  //     if (down && dy > 0 && velocity > 0.2) {
  //       onClose();
  //     } else {
  //       api.start({ y: down ? my : 0 });
  //     }
  //   },
  //   { from: () => [0, y.get()] }
  // );

  const [isDepthExpanded, setIsDepthExpanded] = useState(true);
  const [lots, setLots] = useState(1);
  const [quantity, setQuantity] = useState(stock.lotsize || 1);
  const [orderType, setOrderType] = useState('market');
  const [isLimitOrder, setIsLimitOrder] = useState(false);
  const [price, setPrice] = useState('');

  const toggleDepth = () => {
    setIsDepthExpanded(!isDepthExpanded);
  };

  const prevLotsRef = useRef(lots);
  const prevQuantityRef = useRef(quantity);
  const isManualChangeRef = useRef(false);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !isInitializedRef.current) {
      // Initialize values based on the stock's lot size when the bottom sheet opens
      const initialLotSize = stock.lotsize || 1;
      setQuantity(initialLotSize);
      setLots(1);
      prevLotsRef.current = 1;
      prevQuantityRef.current = initialLotSize;
      isInitializedRef.current = true;
    } else if (!isOpen) {
      isInitializedRef.current = false;
    }
  }, [isOpen, stock.lotsize]);

  // Update quantity when lot size changes - DISABLED to prevent interference
  useEffect(() => {
    // Completely disabled to prevent lot reset
    // Manual quantity update in increment/decrement functions
  }, [lots]);

  // Update lot size when quantity changes (only for manual quantity input)
  useEffect(() => {
    // Completely disabled to prevent lot reset - manual quantity input only
    isManualChangeRef.current = false;
  }, [quantity]);

  // Reset refs when bottom sheet opens
  useEffect(() => {
    if (isOpen && isInitializedRef.current) {
      prevLotsRef.current = lots;
      prevQuantityRef.current = quantity;
    }
  }, [isOpen]);

  const incrementLots = () => {
    console.log("Increment lots clicked, current lots:", lots);
    isManualChangeRef.current = true;
    const newLots = lots + 1;
    console.log("Setting lots to:", newLots);
    setLots(newLots);
    // Force update the ref immediately
    prevLotsRef.current = newLots;
    // Force re-render by updating quantity
    const newQuantity = newLots * (stock.lotsize || 1);
    setQuantity(newQuantity);
  };

  const decrementLots = () => {
    console.log("Decrement lots clicked, current lots:", lots);
    isManualChangeRef.current = true;
    if (lots > 1) {
      const newLots = lots - 1;
      console.log("Setting lots to:", newLots);
      setLots(newLots);
      // Force update the ref immediately
      prevLotsRef.current = newLots;
      // Force re-render by updating quantity
      const newQuantity = newLots * (stock.lotsize || 1);
      setQuantity(newQuantity);
    }
  };

  const incrementQuantity = () => {
    isManualChangeRef.current = true;
    if(stock.exchange != "OPTSTK"){
      const newQuantity = Number(quantity) + 1;
      setQuantity(newQuantity);
      // Update lots based on new quantity
      const newLots = newQuantity / (stock.lotsize || 1);
      setLots(newLots);
    }
  };

  const decrementQuantity = () => {
    isManualChangeRef.current = true;
    if(stock.exchange != "OPTSTK"){
      if(quantity > 1){
        const newQuantity = Number(quantity) - 1;
        setQuantity(newQuantity);
        // Update lots based on new quantity
        const newLots = newQuantity / (stock.lotsize || 1);
        setLots(newLots);
      }
    }
  };

  const handleOrderTypeToggle = () => {
    setIsLimitOrder(!isLimitOrder);
    //alert("Limit Order: "+!isLimitOrder)
  };
  

  // Calculate the change percentage
  const _Bid = stock.buyPrice = _stock.Bid!=undefined ? _stock.Bid : stock.buyPrice;
  const _Ask = stock.sellPrice = _stock.Ask!=undefined ? _stock.Ask : stock.sellPrice;
  const _Open = _stock.Open!=undefined ? _stock.Open : stock.open;
  const _High = _stock.High!=undefined ? _stock.High : stock.high;
  const _Low = _stock.Low!=undefined ? _stock.Low : stock.low;
  const _Prev_Close = _stock.Prev_Close!=undefined ? _stock.Prev_Close : stock.close;
  const _LTP = _stock.LTP!=undefined ? _stock.LTP : stock.lastTradedPrice;

  const Change_per = ((_LTP - _Prev_Close) / _Prev_Close * 100).toFixed(2);
  const Change = (_LTP - _Prev_Close).toFixed(2);
  const changeColor = Change > 0 ? "green" : "red";

  const buyDepth = stock.live.depth?.buy || [];
  const sellDepth = stock.live.depth?.sell || [];

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('backdrop')) {
      onClose();
    }
  };

  const handleOrderTypeChange = (event, newOrderType) => {
    setOrderType(newOrderType);
  };

  const handleQuantityChange = (event) => {
    isManualChangeRef.current = true;
    const newQuantity = parseInt(event.target.value) || 0;
    setQuantity(newQuantity); // Update quantity state with the new input value
    
    // Update lots based on new quantity
    const newLots = newQuantity / (stock.lotsize || 1);
    setLots(newLots);
    
    // const value = event.target.value;
    // console.log("I am working");
    // if (!isNaN(value) && value >= 0) {
    //   setQuantity(value);
    // }
    // else{
    //   setQuantity('');
    // }
  };

  const createOrder_old = (orderType) => {
    const orderDetails = {
      stock,
      quantity,
      price: isLimitOrder === true ? price : (orderType === 'buy' ? _Ask : _Bid),
      orderType
    };
    console.log(orderDetails); // Replace this with your order creation logic
  };

  const createOrder = async (orderType) => {
    
    setDisable(true);
    
    setOrderResponse(null);
    setShowOrderResponse(false);

    if (userDetails.mainAmount <= 0) {
      alert("You don't have balance.");
      setDisable(false);
      setOrderLoading(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"You do not have balance."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);

      return;
    }

    if (quantity < 1) {
      alert("Quantity should be at least 1.");
      setDisable(false);
      setOrderLoading(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Quantity should be at least 1."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);

      return;
    }

    if(stock.exchange === "OPTSTK"){
      if (typeof lots === 'number' && lots % 1 !== 0) {
        // console.log("The value is in points (decimal).");

        alert("Lots should not be in points.");
        setDisable(false);
        setOrderLoading(false);

        var data = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"'+stock.dispname+'"',
          stockExpiry: '"'+stock.expiry+'"',
          orderTransactionType: 1,
          orderExecutionType: isLimitOrder ? 2 : 1,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"Lots should not be in points."',
          orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
        });

        submitRejectionLog(data);

        return;

      }
    }

    if (orderType === 2 && stock.exchange === "OPTSTK") {
      if (Number(userDetails.availableMargin) < Number(userDetails.OPTSTK_balance)) {
        alert(`You don't have sufficient margin to sell.\nMinimum ${userDetails.OPTSTK_balance} is required.`);
        setDisable(false);
        setOrderLoading(false);

        var data = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"'+stock.dispname+'"',
          stockExpiry: '"'+stock.expiry+'"',
          orderTransactionType: 1,
          orderExecutionType: isLimitOrder ? 2 : 1,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"You don not have sufficient margin to sell."',
          orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
        });
  
        submitRejectionLog(data);

        return;
      }
    }

    // const nowTime = new Date();
    // const lastTradeTimeUnix = new Date(stock.lastTradeTime * 1000);
    // const timeDiffInSecond = Math.ceil((nowTime - lastTradeTimeUnix) / 1000);

    // if (stock.autoCutDate <= new Date().toISOString().split('T')[0]) {
    //   alert("You can't trade on autocut date.\nOpened position can be closed from portfolio.");
    //   var data = JSON.stringify({
    //     clientId: userDetails.clientId,
    //     parent_id: userDetails.parentId,
    //     stockId: stock.instrumentIdentifier,
    //     stockName: '"'+stock.dispname+'"',
    //     stockExpiry: '"'+stock.expiry+'"',
    //     orderTransactionType: 1,
    //     orderExecutionType: isLimitOrder ? 2 : 1,
    //     buy_sell: orderType,
    //     lot: lots,
    //     quantity: quantity,
    //     remark: '"You can not trade on autocut date.\nOpened position can be closed from portfolio."',
    //     orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
    //   });

    //   submitRejectionLog(data);
    //   return;
    // }

    if (stock.buyPrice <= 0 || stock.sellPrice <= 0) {
      alert("Price not updated, please refresh");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Price not updated, please refresh."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);
      
      return;
    }

    if (userDetails.availableMargin <= 0) {
      alert("You do not have sufficient margin.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"You do not have sufficient margin."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);
      return;
    }

    let orderAmount = 0;

    if (isLimitOrder) {

      if(price == _LTP){
          alert("Order price should not be equal to LTP.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price should not be equal to LTP."',
            orderPrice: price
          });
    
          submitRejectionLog(data);

          return;
      }

      orderAmount = quantity * price;

      if(Number(price) < Number(stock.lower_circuit) || Number(price) > Number(stock.upper_circuit)) {
          alert("Order price is beyond daily price range."+ stock.lower_circuit + " to " + stock.upper_circuit);
          
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price is beyond daily price range"',
            orderPrice: price
          });

          submitRejectionLog(data);

          return;
      }

      if (orderType === 1) {
        if (price >= stock.buyPrice) {
          alert("Order price must be less than bid price.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price must be less than bid price"',
            orderPrice: price
          });

          submitRejectionLog(data);

          return;
        }
      } else {
        if (price <= stock.sellPrice) {
          alert("Order price should be greater than ask price.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price should be greater than ask price"',
            orderPrice: price
          });

          submitRejectionLog(data);

          return;
        }
      }
    }

    if (orderType === 2) 
    {
        if (stock.buyPrice <= 0) 
        {
            alert("Price not updated, please refresh");
            setDisable(false);

            var data = JSON.stringify({
              clientId: userDetails.clientId,
              parent_id: userDetails.parentId,
              stockId: stock.instrumentIdentifier,
              stockName: '"'+stock.dispname+'"',
              stockExpiry: '"'+stock.expiry+'"',
              orderTransactionType: 1,
              orderExecutionType: isLimitOrder ? 2 : 1,
              buy_sell: orderType,
              lot: lots,
              quantity: quantity,
              remark: '"Price not updated, please refresh."',
              orderPrice: stock.buyPrice
            });
  
            submitRejectionLog(data);

            return;
        }
        orderAmount = quantity * stock.buyPrice;
    } 
    else if (orderType === 1) {
      if (stock.sellPrice <= 0) {
          alert("Price not updated, please refresh");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: isLimitOrder ? 2 : 1,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Price not updated, please refresh."',
            orderPrice: stock.sellPrice
          });

          submitRejectionLog(data);

          return;
      }
      orderAmount = quantity * stock.sellPrice;
    }

    let requiredMargin = 0;

    if (stock.exchange === "FUTCOM") {
      if(userDetails.FUTCOM_margin_type === "script_wise")
      {
        console.log(stock.scriptName);
        console.log(userDetails);

        // Parse the script wise margin data and get interday value
        const scriptMarginData = JSON.parse(userDetails.script_wise_margin[stock.scriptName]);
        const scriptInterdayValue = scriptMarginData.interaday;
        console.log("Script interday value:", scriptInterdayValue);
        
        // Calculate required margin using script wise interday value
        requiredMargin = scriptInterdayValue;
        // return;
      }
      else{
        requiredMargin = orderAmount / userDetails.FUTCOM_intraday;
      }
    } else if (stock.exchange === "FUTSTK") {
      const futstkScripts = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'];
      if(userDetails.FUTSTK_margin_type === "script_wise" && futstkScripts.includes(stock.scriptName))
      {
        const scriptMarginData = JSON.parse(userDetails.script_wise_margin[stock.scriptName]);
        const scriptInterdayValue = scriptMarginData.interaday;
        requiredMargin = scriptInterdayValue;
      }
      else{
        requiredMargin = orderAmount / userDetails.FUTSTK_intraday;
      }
    } else if (stock.exchange === "OPTSTK") {
      requiredMargin = orderAmount / userDetails.OPTSTK_intraday;
    }

    if (orderType === 2 && stock.exchange === "OPTSTK") {
      requiredMargin = lots * userDetails.OPTSTK_balance;
    }

    if (requiredMargin > userDetails.availableMargin) {
      alert(`Can't place order.\nRequired margin: ${Number(requiredMargin).toFixed(2)}, current margin: ${Number(userDetails.availableMargin).toFixed(2)}`);
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Can not place order. Required margin: '+Number(requiredMargin).toFixed(2)+', current margin: '+Number(userDetails.availableMargin).toFixed(2)+'"',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);

      return;
    }

    if (lots <= 0 || lots <= 0.0) {
      alert("Please enter lot quantity.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Please enter lot quantity."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);

      return;
    }

    stock.dispName = stock.dispname;

    setOrderLoading(true);

    try {
      const response = await fetch(`${config.APP_BASE_URL}mediator_rfs.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stock,
          orderTypeBuySell: orderType,
          orderExecutionType: isLimitOrder ? 2 : 1,
          lots: lots,
          orderTransactionType: 1,
          market_type: `${stock.exchange}_intraday`,
          limitSlPrice: price,
          orderDevice: 1,
          user_id: userDetails.clientId,
          parent_id: userDetails.parentId,
          ip: process.env.REACT_APP_USER_IP
        }),
      });
      // console.log("data",response);
      const data = await response.json();


      if (!data.success) {
        
        setDisable(false);
        setOrderLoading(false);
        
        var msg = data.message;
        
        if (data.message === "open position") {
          msg = "Please close position from portfolio.";
        } else if (data.message === "open pending position") {
          msg = "You have a pending limit order on this script.\nYou can cancel it from Trades."
        } else if (data.message.includes("Blocked")) {
          msg = "Trading not allowed on this script.";
        } else {
          msg = data.message;
        }

        setOrderResponse({
          success: false,
          message: msg,
          type: 'error'
        });
        setShowOrderResponse(true);
        
        var dataa = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"'+stock.dispname+'"',
          stockExpiry: '"'+stock.expiry+'"',
          orderTransactionType: 1,
          orderExecutionType: isLimitOrder ? 2 : 1,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"'+msg+'"',
          orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
        });
  
        submitRejectionLog(dataa);
      } 
      else 
      {
        setLots(1);
        setDisable(false);
        setOrderLoading(false);

        var b_s = orderType == 1 ? 'Buy' : 'Sell';
        var msg = b_s+" "+Number(lots).toFixed(2)+" Lot(s) of "+stock.dispName+" @"+Number(data.orderPrice).toFixed(2);
        
        setOrderResponse({
          success: true,
          message: msg,
          type: 'success',
          orderPrice: data.orderPrice,
          orderType: b_s,
          lotSize: lots,
          stockName: stock.dispName,
          isLimitOrder: isLimitOrder
        });
        setShowOrderResponse(true);
        
        refreshTradesAndPortfolio('both');

      }
    } catch (error) {
      console.error("Error:", error);
      setDisable(false);
      setOrderLoading(false);
      setOrderResponse({
        success: false,
        message: "Network error occurred. Please try again.",
        type: 'error'
      });
      setShowOrderResponse(true);
    }
  };

  return (
    <>
      <ToastContainer />
      
      {/* Order Loading Overlay */}
      {orderLoading && (
        <div className="order-loading-overlay">
          <div className="order-loading-container">
            <div className="order-loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <div className="order-loading-content">
              <h3 className="order-loading-title">Placing Order...</h3>
              <p className="order-loading-subtitle">Please wait while we process your order</p>
            </div>
          </div>
        </div>
      )}

      {/* Order Response Modal */}
      {showOrderResponse && orderResponse && (
        <div className="order-response-overlay" onClick={() => setShowOrderResponse(false)}>
          <div className="order-response-container" onClick={(e) => e.stopPropagation()}>
            <div className={`order-response-header ${orderResponse.type}`}>
              <div className="response-icon">
                {orderResponse.success ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <button className="response-close" onClick={() => setShowOrderResponse(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="order-response-content">
              <h3 className={`response-title ${orderResponse.type}`}>
                {orderResponse.success ? 
                  (orderResponse.isLimitOrder ? 'Limit Order Placed Successfully!' : 'Order Placed Successfully!') 
                  : 'Order Failed'}
              </h3>
              <p className="response-message">{orderResponse.message}</p>
              {orderResponse.success && (
                <div className="order-details">
                  <div className="detail-row">
                    <span className="detail-label">Order Type:</span>
                    <span className={`detail-value ${orderResponse.orderType === 'Buy' ? 'buy' : 'sell'}`}>
                      {orderResponse.orderType}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Quantity:</span>
                    <span className="detail-value">{orderResponse.lotSize} Lot(s)</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Script:</span>
                    <span className="detail-value">{orderResponse.stockName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Price:</span>
                    <span className="detail-value">₹{Number(orderResponse.orderPrice).toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Execution:</span>
                    <span className="detail-value">{orderResponse.isLimitOrder ? 'Limit Order' : 'Market Order'}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="order-response-actions">
              <button 
                className="response-button primary" 
                onClick={() => {
                  setShowOrderResponse(false);
                  if (orderResponse.success) {
                    onClose();
                  }
                }}
              >
                {orderResponse.success ? 'Continue Trading' : 'Try Again'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`expanded-view ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="expanded-header">
          <div className="script-name" style={{ margin: '0px'}}>{stock.dispname}</div>
          <div className="order-type-tabs">
            <button 
              className={`order-tab ${!isLimitOrder ? 'active' : ''}`}
              onClick={() => setIsLimitOrder(false)}
            >
              Market
            </button>
            <button 
              className={`order-tab ${isLimitOrder ? 'active' : ''}`}
              onClick={() => setIsLimitOrder(true)}
            >
              Limit
            </button>
          </div>
        </div>
        
        <div className="order-params" style={{ marginBottom: '10px'}}>
          <div className="param-row">
            <span className="param-label">O: {Number(_Open).toFixed(2)}</span>
            <span className="param-label">M: 5</span>
            <span className="param-label">P: 20</span>
            <span className="param-label">C: {Number(_Prev_Close).toFixed(2)}</span>
          </div>
        </div>
        <div className="order-inputs">
          <div className="input-group">
            <label>LOT</label>
            <div className="input-with-buttons">
              <button className="plus-minus-btn" onClick={decrementLots}>-</button>
              <input 
                type="number" 
                value={Number(lots).toFixed(2)} 
                disabled
                className="lot-input"
              />
              <button className="plus-minus-btn" onClick={incrementLots}>+</button>
            </div>
          </div>
          <div className="input-group">
            <label>QTY</label>
            <div className="input-with-buttons">
              <button className="plus-minus-btn" onClick={decrementQuantity}>-</button>
              {stock.exchange === "OPTSTK" ? (
                <input 
                  type="number" 
                  value={Number(quantity)} 
                  disabled
                  className="qty-input"
                />
              ) : (
                <input 
                  type="number" 
                  value={Number(quantity)} 
                  onChange={handleQuantityChange}
                  className="qty-input"
                />
              )}
              <button className="plus-minus-btn" onClick={incrementQuantity}>+</button>
            </div>
          </div>
        </div>
        {isLimitOrder && (<div className="order-inputs">
          
            <div className="input-group">
              <label>Rate</label>
              <input 
                type="number" 
                value={price} 
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Enter rate"
                className="rate-input"
                style={{ width: '150px'}}
              />
            </div>
          
        </div>)}
        <div className="order-buttons" style={{ flexDirection: 'row', marginBottom: '0px'}}>
          {((userDetails.OPTSTK_sellAllow == 1 && stock.exchange === "OPTSTK") || stock.exchange !== "OPTSTK") && (
            <button 
              className="sell-btn"
              onClick={() => createOrder(2)}
              disabled={disableBtn}
            >
              Sell {isLimitOrder ? '' : `@ ${Number(_Bid).toFixed(2)}`}
            </button>
          )}
          <button 
            className="buy-btn"
            onClick={() => createOrder(1)}
            disabled={disableBtn}
          >
            Buy {isLimitOrder ? '' : `@ ${Number(_Ask).toFixed(2)}`}
          </button>
        </div>
        
        {/* <div className="margin-info">
          <p>Intraday Margin: {stock.exchange === 'OPTSTK' ? 
            (lots * userDetails.OPTSTK_balance).toFixed(2) : 
            ((quantity * (isLimitOrder ? (price || _Bid) : _Bid)) / (stock.intraday || 1)).toFixed(2)
          }</p>
        </div> */}
      </div>
    </>
  );
};

export default BottomSheetMini;
